import type { AddressbookTranslations } from './types'

/**
 * Arabic translations for Addressbook domain
 */
export const arTranslations: AddressbookTranslations = {
  // Page/Section level
  title: "دفتر العناوين",
  subtitle: "إدارة المواقع والعناوين الخاصة بك",
  
  // Actions
  addAddress: "إضافة عنوان جديد",
  editAddress: "تعديل العنوان",
  deleteAddress: "حذف العنوان",
  exportComingSoon: "ستتوفر وظيفة التصدير قريباً.",
  
  // Drawer Headers
  addAddressDescription: "أدخل التفاصيل لإضافة عنوان جديد",
  editAddressDescription: "قم بتحديث تفاصيل العنوان أدناه",
  
  // Section Headers
  basicInfo: "المعلومات الأساسية",
  addressDetails: "تفاصيل العنوان",
  contactInfo: "معلومات الاتصال",
  coordinates: "الإحداثيات",
  
  // Search and states
  searchPlaceholder: "البحث في العناوين...",
  noAddresses: "لا توجد عناوين",
  noAddressesDescription: "لم تقم بإضافة أي عناوين بعد. أضف عنوانك الأول للبدء.",
  addFirst: "أضف عنوانك الأول",
  
  // Form fields
  locationName: "اسم الموقع",
  locationNamePlaceholder: "مثال: المكتب الرئيسي، المستودع أ",
  locationType: "نوع الموقع", 
  selectType: "اختر نوع الموقع",
  selectLocationType: "اختر نوع الموقع (اختياري)",
  noLocationType: "بدون نوع موقع",
  streetAddress: "عنوان الشارع",
  streetAddressPlaceholder: "أدخل عنوان الشارع، اسم المبنى، إلخ.",
  region: "المنطقة",
  selectRegion: "اختر المنطقة",
  city: "المدينة",
  selectCity: "اختر المدينة",
  selectRegionFirst: "اختر المنطقة أولاً",
  country: "الدولة",
  selectCountry: "اختر الدولة",
  selectCountryFirst: "اختر الدولة أولاً",
  noCitiesAvailable: "لا توجد مدن متاحة",
  postalCode: "الرمز البريدي",
  postalCodePlaceholder: "أدخل الرمز البريدي",
  latitude: "خط العرض",
  longitude: "خط الطول",
  contactName: "اسم جهة الاتصال",
  contactNamePlaceholder: "أدخل اسم الشخص المسؤول",
  contactPhone: "هاتف جهة الاتصال", 
  contactPhonePlaceholder: "+966 50 XXX XXXX",
  contactEmail: "البريد الإلكتروني",
  address: "العنوان",
  notes: "ملاحظات",
  tags: "العلامات", 
  timezone: "المنطقة الزمنية",
  
  // Address Types
  site: "موقع",
  office: "مكتب",
  warehouse: "مستودع",
  home: "منزل",
  plant: "مصنع",
  customer: "عميل",
  
  // Status
  defaultAddress: "العنوان الافتراضي",
  favorite: "مفضل",
  
  // Toast Messages
  addressCreated: "تم إنشاء العنوان",
  addressCreatedDescription: "تمت إضافة العنوان الجديد بنجاح.",
  addressUpdated: "تم تحديث العنوان",
  addressUpdatedDescription: "تم تحديث تفاصيل العنوان.",
  addressDeleted: "تم حذف العنوان",
  addressDeletedDescription: "تمت إزالة العنوان.",
  
  // Delete Dialog
  deleteTitle: "حذف العنوان", 
  deleteDescription: "هل أنت متأكد من حذف هذا العنوان؟ لا يمكن التراجع عن هذا الإجراء.",
  deleteConfirm: "نعم، احذف",
  
  // Filter Options
  filterAll: "جميع الأنواع",
  filterSite: "المواقع",
  filterOffice: "المكاتب",
  filterWarehouse: "المستودعات", 
  filterHome: "المنازل",
  filterPlant: "المصانع",
  filterCustomer: "العملاء",
  
  // Stats
  totalAddresses: "إجمالي العناوين",
  activeAddresses: "نشط",
  inactiveAddresses: "غير نشط",
  
  // Table Headers
  table: {
    name: "الاسم",
    type: "النوع",
    address: "العنوان",
    city: "المدينة",
    contact: "جهة الاتصال",
    status: "الحالة"
  },
  
  // Contact Types
  contactType: {
    gate_manager: "مدير البوابة",
    dock_supervisor: "مشرف الرصيف",
    security: "الأمن",
    primary_contact: "جهة الاتصال الرئيسية",
    emergency_contact: "جهة اتصال الطوارئ"
  },
  
  // Location Type descriptions (extended)
  locationTypeDescriptions: {
    site: "موقع بناء",
    office: "مبنى مكاتب",
    warehouse: "مستودع",
    home: "عنوان المنزل",
    plant: "مصنع تصنيع",
    customer: "موقع العميل"
  },
  
  // Validation
  validation: {
    nameRequired: "اسم الموقع مطلوب",
    addressRequired: "عنوان الشارع مطلوب",
    countryRequired: "الدولة مطلوبة",
    cityRequired: "المدينة مطلوبة"
  },
  
  // Additional Form
  form: {
    englishSection: "الإنجليزية",
    streetSection: "عنوان الشارع",
    locationNameEn: "اسم الموقع (إنجليزي)",
    locationNameAr: "اسم الموقع (عربي)",
    locationNameArPlaceholder: "الاسم بالعربية (اختياري)",
    streetAddressEn: "عنوان الشارع (إنجليزي)",
    streetAddressAr: "عنوان الشارع (عربي)",
    streetAddressArPlaceholder: "عنوان الشارع بالعربية (اختياري)",
    addressLine2: "سطر العنوان 2",
    addressLine2Placeholder: "شقة، جناح، وحدة، إلخ. (اختياري)",
    addressLine2En: "سطر العنوان 2 (إنجليزي)",
    addressLine2Ar: "سطر العنوان 2 (عربي)",
    addressLine2ArPlaceholder: "سطر 2 بالعربية (اختياري)",
  },

  // Additional keys
  addressId: "معرف العنوان",
  createNewLocation: "إنشاء موقع جديد",
  createNewLocationHint: "إضافة عنوان توصيل جديد",
  searchLocations: "البحث عن المواقع...",
  noMatchingAddresses: "لا توجد عناوين مطابقة",
  noAddressesFound: "لم يتم العثور على عناوين",

  // Widget/Stats keys
  locations: "المواقع",
  sites: "المواقع",
  offices: "المكاتب",
  total: "الإجمالي",
  active: "نشط",
  inactive: "غير نشط",

  // Table column keys
  name: "الاسم",
  actions: "الإجراءات",

  // Delete confirmation with placeholder
  deleteConfirmation: "هل أنت متأكد من حذف {{name}}؟ لا يمكن التراجع عن هذا الإجراء.",

  // Standalone convenience keys (duplicates of nested keys for easier access)
  type: "النوع",
  contact: "جهة الاتصال",
  status: "الحالة",

  // Contact type standalone keys (duplicates of contactType for easier access)
  gate_manager: "مدير البوابة",
  dock_supervisor: "مشرف الرصيف",
  security: "الأمن",
  primary_contact: "جهة الاتصال الرئيسية",
  emergency_contact: "جهة اتصال الطوارئ",

  // Validation standalone keys (duplicates of validation for easier access)
  nameRequired: "اسم الموقع مطلوب",
  addressRequired: "عنوان الشارع مطلوب",
  countryRequired: "الدولة مطلوبة",
  cityRequired: "المدينة مطلوبة",

  // Form standalone keys (duplicates of form for easier access)
  addressLine2: "سطر العنوان 2",
  addressLine2Placeholder: "شقة، جناح، وحدة، إلخ. (اختياري)",
}
